import { Scroll } from "./scroll";
import { Dom } from "./dom";
import { Preloader } from "./persistent/preloader";
import { Cursor } from "./persistent/cursor";
import { BackgroundMusic } from "./persistent/background-music";
import { isMobile, isLargeScreen } from "./utils/media";
import { Pages } from "./pages";
import hey from "./hey";

/**
 * App - Main Application Controller
 *
 * Orchestrates the entire application lifecycle.
 *
 * CRITICAL: The order matters to prevent "flash" of unstyled content:
 *
 * ┌─────────────────────────────────────────────────────────────┐
 * │ FIRST VISIT (onepager with logo intro):                     │
 * │ 1. Scroll.stop() → Lock scrolling                           │
 * │ 2. Dom.create() → Modules set hidden state                  │
 * │ 3. Preloader.init() → Logo animation plays                  │
 * │    └─ On complete: Dom.start() + Nav fades in               │
 * │ 4. Scroll.start() → Unlock scrolling                        │
 * └─────────────────────────────────────────────────────────────┘
 *
 * ┌─────────────────────────────────────────────────────────────┐
 * │ NAVIGATION (via Taxi):                                      │
 * │ 1. Dom.transitionOut() → Modules animate out & destroy      │
 * │ 2. Page swap                                                │
 * │ 3. Dom.transitionIn() → Create + Start new modules          │
 * └─────────────────────────────────────────────────────────────┘
 */
class _App {
  // Core instances (set after init)
  scroll = null;
  pages = null;
  dom = null;
  preloader = null;

  // Device detection (updated on resize)
  isMobile = isMobile();
  isLargeScreen = isLargeScreen();

  // State
  #initialized = false;

  constructor() {
    // Auto-initialize when DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      this.init();
    }

    // Scroll to top before page unloads (for reload)
    window.addEventListener("beforeunload", () => {
      window.scrollTo(0, 0);
    });
  }

  /**
   * Initialize the application
   * This is the main entry point
   */
  async init() {
    if (this.#initialized) return;
    this.#initialized = true;

    console.log("🚀 App initializing...");

    // Disable browser's scroll restoration on reload
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Custom cursor from the start (hides default cursor before preloader)
    Cursor.init();

    // Store references
    this.scroll = Scroll;
    this.dom = Dom;
    this.preloader = Preloader;

    await document.fonts.ready;


    // Stop scroll during preloader
    Scroll.stop();
    Scroll.toTop();


    // ═══════════════════════════════════════════════════════════
    // PHASE 1: Create modules BEFORE logo animation
    // Modules set their initial hidden state (yPercent: 110, etc.)
    // This prevents the "flash" when elements become visible
    // ═══════════════════════════════════════════════════════════
    Dom.create();

    // ═══════════════════════════════════════════════════════════
    // PHASE 2: Run preloader (logo intro animation)
    // - Sets up logo elements in hidden state
    // - Plays logo animation
    // - On complete: calls Dom.start() + fades in navigation
    // ═══════════════════════════════════════════════════════════
    await Preloader.init();

    // Background music (mini player + Howler) starts after preloader
    BackgroundMusic.init();

    // ═══════════════════════════════════════════════════════════
    // PHASE 3: Unlock scroll after intro completes
    // Dom.start() was already called by Preloader on animation complete
    // ═══════════════════════════════════════════════════════════
    Scroll.start();

    // Initialize Pages (Taxi)
    this.pages = Pages;

    console.log("✨ App ready");
    hey.APP_READY = true;
  }

  /**
   * Update mobile detection (called by Resize subscriber)
   */
  _isMobile = () => {
    this.isMobile = isMobile();
    return this.isMobile;
  };

  /**
   * Update large screen detection (called by Resize subscriber)
   */
  _isLargeScreen = () => {
    this.isLargeScreen = isLargeScreen();
    return this.isLargeScreen;
  };

  /**
   * Skip preloader (useful for development)
   * Note: Still creates modules before skipping to prevent flash
   */
  skipPreloader() {
    Preloader.skip();
  }
}

// Export singleton instance
export const App = new _App();

// Also export class for type checking if needed
export { _App };
